/* eslint no-lonely-if: "off" */
import React, { Fragment } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import PropTypes from 'prop-types'
import { graphql, compose, withApollo } from 'react-apollo'
import DestroyCashflowData from './queries/DestroyCashflowData'
import Segment from './Segment'
import PaneHeader from './PaneHeader'
import Loader from '../common/Loader'
import CreateCashflowDatum from './queries/CreateCashflowDatum'
import FinancialReviewQuery from './queries/FinancialReviewQuery'
import CacheUtil from '../common/utils/CacheUtil'
import BusinessCashflowQuery from './queries/BusinessCashflowQuery'
import GuarantorCashflowQuery from './queries/GuarantorCashflowQuery'
import LoanCashflowQuery from './queries/LoanCashflowQuery'
import CashflowDecisionableQuery from './queries/CashflowDecisionableQuery'
import getYearSources from './helpers/getYearSources'
import updateInnerHTML from '../common/helpers/updateInnerHTML'

const Container = styled.section`
  border: 2px solid #eeeeee;
`
const Small = styled.small`
  margin: 0px 0px 10px 0px;
  color: #888;
  display: block;
`
const Header = styled.h3`
  margin-bottom: 0px;
`
const LoaderContainer = styled.div`
  height: 100%;
  width: 100%;
  background: rgba(255,255,255, 0.4);
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`
const LoanAnalysisContainer = styled.div`
  text-align: right;
`
const LoanAnalysisLink = styled.a`
  cursor: pointer;
  display: block;
  margin-bottom: 5px;
`
const Calculator = styled.i`
  font-size: 17px;
`

export class Pane extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isEditable: false,
      category: null,
      year: null,
      yearSources: getYearSources(props),
      rid: props.entity.rid, // Use rid as a state property to compare with previous props
      years: props.years
    }
    this.cell = 0 // current tabIndex
    this.cells = {} // hash of refs to individual cells
    this.tabIndices = {}
    this.markedForDelete = []
    this.markedForSave = {}
  }
  toggleEdit = () => {
    this.setState({
      isEditable: !this.state.isEditable
    })
  }
  setTabIndices = (props) => {
    const { years, entity } = props
    const { financialReviewSegments, cashflowData } = entity
    let tabIndex = 0
    // Set a tab index for all cells except those in derived categories
    for (const segment of financialReviewSegments) {
      for (const category of segment.categories) {
        // Only make tab indices for categories with data that aren't from tax transcripts
        if (!category.derived && (cashflowData.filter(datum => datum.category === category.key).length > 0 || category.required)) {
          for (const year of years.keys()) {
            this.tabIndices[`${year}-${category.key}`] = tabIndex
            tabIndex += 1
          }
        }
      }
    }
  }
  componentDidMount() {
    this.setTabIndices(this.props)
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.entity.rid !== prevState.rid) {
      return { isEditable: false, yearSources: getYearSources(nextProps), rid: nextProps.entity.rid, years: nextProps.years }
    } else {
      return null
    }
  }
  componentDidUpdate(_, prevState) {
    this.tabIndices = {}
    this.setTabIndices(this.props)
    this.cell = 0
    if (prevState.isEditable === true && this.state.isEditable === false) {
      this.commitCashflowUpdates()
    }
  }
  resetYearSources = () => {
    this.setState({ yearSources: getYearSources(this.props) })
  }
  markForDelete = (year, category) => {
    this.markedForDelete.push({ year, category })
    const markedForSave = this.markedForSave[`${year}${category}`]
    if (markedForSave) {
      delete this.markedForSave[markedForSave]
    }
  }
  saveFromDelete = (year, category) => {
    const idx = this.markedForDelete.findIndex(marked => marked.year === year && marked.category === category)
    if (idx > -1) {
      this.markedForDelete.splice(idx, 1)
    }
  }
  deleteCashflowData = async () => {
    const { destroyCashflowData, entity, resourceType } = this.props
    if (this.markedForDelete.length > 0) {
      this.updateDeletedCashflowData(this.markedForDelete)
      return destroyCashflowData({
        variables: {
          resourceType,
          resourceId: entity.rid,
          dataKeys: this.markedForDelete
        }
      })
    }
  }
  markForSave = (year, category, datum) => {
    this.markedForSave[`${year}${category}`] = datum
    const markedForDelete = this.markedForDelete.findIndex(d => d.year === year && d.category === category)
    if (markedForDelete < 0) {
      this.markedForDelete.splice(markedForDelete, 1)
    }
  }
  saveCashflowData = async () => {
    const { createCashflowDatum, entity, resourceType } = this.props
    const cashflowData = Object.values(this.markedForSave)
    if (cashflowData.length > 0) {
      this.updateSavedCashflowData(cashflowData)
      const mutations = cashflowData.map(d => {
        const { category, data, year, reason, source } = d
        return createCashflowDatum({
          variables: {
            input: {
              cashflowDatumFields: {
                category,
                data,
                year,
                resourceId: entity.rid,
                resourceType,
                reason,
                source
              }
            }
          }
        })
      })
      return Promise.all(mutations)
    }
  }
  dontSave = (year, category) => {
    delete this.markedForSave[`${year}${category}`]
  }
  updateSavedCashflowData = cashflowData => {
    const { queriedYears, cashflowOptions, loanId, client, entity, resourceType } = this.props
    // First get a list of all years covered by the cashflow data
    const queryVariables = {
      query: FinancialReviewQuery,
      variables: {
        loanId,
        cashflowOptions,
        years: queriedYears
      }
    }
    const data = client.readQuery({ ...queryVariables })
    const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
    for (const datum of cashflowData) {
      const d = {
        ...datum,
        resourceType,
        createdAt: moment().unix(),
        __typename: 'CashflowDatum',
        accountable: {
          name: '',
          __typename: 'Administrator'
        }
      }
      CacheUtil.replaceCashflowDatum(d, cachedEntity)
    }
    client.writeQuery({ ...queryVariables, data })
  }
  updateDeletedCashflowData = dataKeys => {
    const { queriedYears, cashflowOptions, loanId, client, entity } = this.props
    const queryVariables = {
      query: FinancialReviewQuery,
      variables: {
        loanId,
        cashflowOptions,
        years: queriedYears
      }
    }
    const data = client.readQuery({ ...queryVariables })
    const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
    for (const key of dataKeys) {
      CacheUtil.removeCashflowDataByYearAndCategory(key, cachedEntity)
    }
    client.writeQuery({ ...queryVariables, data })
  }
  updateDerivedCashflowData = async years => {
    const { queriedYears, cashflowOptions, loanId, client, entity, resourceType } = this.props
    // To save on DB reads, we pick which query to run based on which type of entity we are updating
    const entityQueryVariables = {
      query: resourceType === 'Business' ? BusinessCashflowQuery : GuarantorCashflowQuery,
      variables: {
        rid: entity.rid,
        cashflowOptions,
        years
      },
      fetchPolicy: 'no-cache'
    }
    // We always need to query the loan because Global DSCRs will change
    const loanQueryVariables = {
      query: LoanCashflowQuery,
      variables: {
        ...entityQueryVariables.variables,
        rid: loanId
      },
      fetchPolicy: 'no-cache'
    }
    const cacheQueryVariables = {
      query: FinancialReviewQuery,
      variables: {
        cashflowOptions,
        loanId,
        years: queriedYears
      }
    }
    const data = client.readQuery({ ...cacheQueryVariables })
    const entityQuery = await client.query(entityQueryVariables)
    const loanQuery = await client.query({ ...loanQueryVariables })
    const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
    // TODO: Find out if there is a better way to access data in the entityQuery
    // Filter the cached cashflow data and concat the queried cashflow data
    cachedEntity.cashflowData = cachedEntity.cashflowData.filter(d => !years.includes(d.year))
      .concat(entityQuery.data[resourceType.toLowerCase()].cashflowData)
    data.loan.cashflowData = data.loan.cashflowData.filter(d => !years.includes(d.year))
      .concat(loanQuery.data.loan.cashflowData)
    client.writeQuery({ ...cacheQueryVariables, data })
  }
  commitCashflowUpdates = async () => {
    const { entity } = this.props
    const { fiscalYears } = entity
    await this.deleteCashflowData()
    await this.saveCashflowData()
    // Previously we would only refetch the years that were updated, however if there are any guarantor interims we
    // have to refetch data for every year so global values are calculated properly
    const yearsToRefetch = fiscalYears.map(fy => fy.year)
    if (yearsToRefetch.length > 0) {
      await this.updateDerivedCashflowData(yearsToRefetch)
    }
    await this.updateCashflowDecisionable()
    this.markedForSave = {}
    this.markedForDelete = []
  }
  updateCashflowDecisionable = async () => {
    const { client, loan, cashflowOptions, queriedYears } = this.props
    const queryVariables = {
      cashflowOptions,
      years: queriedYears,
      loanId: loan.rid
    }
    const {
      data: {
        loan: {
          cashflowDecisionable,
          needsRedecision
        }
      }
    } = await client.query({ fetchPolicy: 'network-only', query: CashflowDecisionableQuery, variables: { rid: loan.rid } })
    if (needsRedecision && cashflowDecisionable && document.getElementById('default_probability').innerHTML.length > 0) {
      updateInnerHTML('default_probability', '?')
    }
    const cache = client.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
    cache.loan.cashflowDecisionable = cashflowDecisionable
    cache.loan.needsRedecision = needsRedecision
    client.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data: cache })
  }
  cellRef = (node, tabIndex) => {
    if (node) {
      this.cells[tabIndex] = node
    }
  }
  changeCell = (year, category) => {
    this.setState({ year })
    this.setState({ category })
  }
  // Use category, year to pick which cell to focus. increment year to go right, go to next category to go up and down
  down = () => {
    const { entity } = this.props
    const allCategories = entity.financialReviewSegments.reduce((acc, segment) => acc.concat(segment.categories), [])
      .filter(cat => !cat.derived && entity.cashflowData.some(datum => datum.category === cat) || !cat.derived && cat.required)
      .map(cat => cat.key)
    const nextCategory = allCategories[allCategories.indexOf(this.state.category) + 1]
    if (nextCategory && this.cells[`${this.state.year}${nextCategory}`]) {
      this.cells[`${this.state.year}${nextCategory}`].focus()
      this.setState({ category: nextCategory })
    }
  }
  up = () => {
    const { entity } = this.props
    const allCategories = entity.financialReviewSegments.reduce((acc, segment) => acc.concat(segment.categories), [])
      .filter(cat => !cat.derived && entity.cashflowData.some(datum => datum.category === cat) || !cat.derived && cat.required)
      .map(cat => cat.key)
    const prevCategory = allCategories[allCategories.indexOf(this.state.category) - 1]
    if (prevCategory && this.cells[`${this.state.year}${prevCategory}`]) {
      this.cells[`${this.state.year}${prevCategory}`].focus()
      this.setState({ category: prevCategory })
    }
  }
  right = () => {
    const { entity, years } = this.props
    const allCategories = entity.financialReviewSegments.reduce((acc, segment) => acc.concat(segment.categories), [])
      .filter(cat => !cat.derived && entity.cashflowData.some(datum => datum.category === cat) || !cat.derived && cat.required)
      .map(cat => cat.key)
    const nextCategory = allCategories[allCategories.indexOf(this.state.category) + 1]
    const flatYears = Array.from(years.keys())
    const firstYear = flatYears[0]
    const lastYear = flatYears[flatYears.length - 1]
    const nextYear = this.state.year + 1
    if (nextYear <= lastYear && this.cells[`${nextYear}${this.state.category}`]) {
      this.cells[`${nextYear}${this.state.category}`].focus()
      this.setState({ year: nextYear })
    } else {
      if (nextYear < lastYear) {
        for (let i = nextYear; i <= lastYear; i++) {
          if (this.cells[`${i}${this.state.category}`]) {
            this.cells[`${i}${this.state.category}`].focus()
            this.setState({ year: i })
            return
          }
        }
      }
      for (let i = firstYear; i <= lastYear; i++) {
        if (this.cells[`${i}${nextCategory}`]) {
          this.cells[`${i}${nextCategory}`].focus()
          this.setState({ year: i })
          this.setState({ category: nextCategory })
          break
        }
      }
    }
  }
  left = () => {
    const { entity, years } = this.props
    const allCategories = entity.financialReviewSegments.reduce((acc, segment) => acc.concat(segment.categories), [])
      .filter(cat => !cat.derived && entity.cashflowData.some(datum => datum.category === cat) || !cat.derived && cat.required)
      .map(cat => cat.key)
    const flatYears = Array.from(years.keys())
    const prevCategory = allCategories[allCategories.indexOf(this.state.category) - 1]
    const firstYear = flatYears[0]
    const lastYear = flatYears[flatYears.length - 1]
    const prevYear = this.state.year - 1
    if (prevYear >= firstYear && this.cells[`${prevYear}${this.state.category}`]) {
      this.cells[`${prevYear}${this.state.category}`].focus()
      this.setState({ year: prevYear })
    } else {
      if (prevYear > firstYear) {
        for (let i = prevYear; i >= firstYear; i--) {
          if (this.cells[`${i}${this.state.category}`]) {
            this.cells[`${i}${this.state.category}`].focus()
            this.setState({ year: i })
            return
          }
        }
      }
      for (let i = lastYear; i >= firstYear; i--) {
        if (this.cells[`${i}${prevCategory}`]) {
          this.cells[`${i}${prevCategory}`].focus()
          this.setState({ year: i })
          this.setState({ category: prevCategory })
          break
        }
      }
    }
  }
  changeYearSource = (year, source, period) => {
    this.setState({
      yearSources: {
        ...this.state.yearSources,
        [year]: {
          source,
          period
        }
      }
    })
  }
  onFormOptionsChange = (newFormName, year) => {
    const { years } = this.state
    const { taxReturn } = years.get(year)
    const yearsClone = new Map(years)
    if (newFormName.toLowerCase() === 'interim') { // Not sure if toLowerCase is necessary here, but it may be if we are dealing with the form title
      yearsClone.set(year, { period: 'interim', taxReturn })
    } else {
      yearsClone.set(year, { period: 'tax', taxReturn: { ...taxReturn, formName: newFormName } })
    }
    this.setState({ years: yearsClone })
  }
  getFYEndDate = () => {
    const { resourceType, entity } = this.props
    const { financial } = entity
    if (resourceType === 'Business' && financial) {
      return financial.fiscalYearEndOn
    }
    return null
  }
  onLoanAnalysisClick = () => {
    this.props.toggleTinkerTool()
  }
  renderHeader = () => {
    const {
      resourceType,
      refetch,
      adjustmentsNode,
      entity,
      isEditAllowed,
      cashflowOptions,
      loanId,
      loan,
      queriedYears,
      years
    } = this.props
    const { isEditable, yearSources } = this.state
    const { cashflowData } = this.props.entity
    return (
      <PaneHeader
        onFormOptionsChange={this.onFormOptionsChange}
        years={years}
        toggleEdit={this.toggleEdit}
        isEditable={isEditable}
        save={this.save}
        data={cashflowData}
        resourceType={resourceType}
        refetch={refetch}
        adjustmentsNode={adjustmentsNode}
        pAndLUrls={entity.profitAndLossDocumentUrls}
        interimYears={entity.interimYears && entity.interimYears}
        fiscalYearEndDate={this.getFYEndDate()}
        entity={entity}
        isEditAllowed={isEditAllowed}
        changeYearSource={this.changeYearSource}
        yearSources={yearSources}
        cashflowOptions={cashflowOptions}
        loanId={loanId}
        loan={loan}
        queriedYears={queriedYears}
        resetYearSources={this.resetYearSources}
      />
    )
  }
  renderSegments = () => {
    const { isEditable, yearSources, rid } = this.state
    const { loanId, entity, resourceType, refetch, interimYears, isEditAllowed, interimPeriods, cashflowOptions, loan, queriedYears, years } = this.props
    const { cashflowData, financialReviewSegments } = entity
    return financialReviewSegments.map((segment, index) => {
      if (!segment.key.includes('dscr') && !segment.key.includes('global')) {
        return (
          <Segment
            key={`${segment.title}-${index}`}
            title={segment.title}
            categories={segment.categories}
            data={cashflowData.filter(datum => segment.categories.map(category => category.key).includes(datum.category))}
            fiscalYears={entity.fiscalYears}
            years={years}
            selectedYear={this.state.year}
            selectedCategory={this.state.category}
            isEditable={isEditable}
            entityId={parseInt(rid, 10)}
            entityName={entity.name}
            cellRef={this.cellRef}
            changeCell={this.changeCell}
            index={index}
            moveFns={{ up: this.up, down: this.down, left: this.left, right: this.right }}
            tabIndices={this.tabIndices}
            loanId={loanId}
            segmentKey={segment.key}
            resourceType={resourceType}
            refetch={refetch}
            entity={entity}
            interimYears={interimYears}
            isEditAllowed={isEditAllowed}
            yearSources={yearSources}
            markForDelete={this.markForDelete}
            saveFromDelete={this.saveFromDelete}
            interimPeriods={interimPeriods}
            cashflowOptions={cashflowOptions}
            loan={loan}
            queriedYears={queriedYears}
            markForSave={this.markForSave}
            dontSave={this.dontSave}
            resetYearSources={this.resetYearSources}
          />
        )
      }
    })
  }
  renderLoader = () => {
    const { numberLoading } = this.state
    if (numberLoading > 1) {
      return (
        <LoaderContainer>
          <Loader number={4} />
        </LoaderContainer>
      )
    }
  }
  render() {
    const { isCustomerService } = this.props
    const { name } = this.props.entity
    return (
      <Fragment>
        <Header>Cash Flow Analysis for {name}</Header>
        <Small>
          Italics indicate that a value has been adjusted. Details for each adjustment can be viewed in the Cash and Debt Adjustments table.
        </Small>
        <LoanAnalysisContainer>
          <LoanAnalysisLink id="open-tinker-tool" onClick={this.onLoanAnalysisClick}>
            {
              isCustomerService &&
                <Fragment>
                  <Calculator className="fa fa-calculator" /> Tinker Tool
                </Fragment>
            }
          </LoanAnalysisLink>
        </LoanAnalysisContainer>
        <Container id="pane">
          {this.renderHeader()}
          {this.renderSegments()}
          {this.renderLoader()}
        </Container>
      </Fragment>
    )
  }
}

Pane.propTypes = {
  entity:            PropTypes.object.isRequired,
  isCustomerService: PropTypes.bool,
  isEditAllowed:     PropTypes.bool,
  loan:              PropTypes.object,
  loanId:            PropTypes.number.isRequired,
  refetch:           PropTypes.func.isRequired,
  resourceType:      PropTypes.string,
  toggleTinkerTool:  PropTypes.func,
  years:             PropTypes.object.isRequired
}

export default compose(
  graphql(DestroyCashflowData, {
    name: 'destroyCashflowData'
  }),
  graphql(CreateCashflowDatum, {
    name: 'createCashflowDatum'
  }),
  withApollo
)(Pane)
